import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, forkJoin, isObservable, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class TranslateService {

  data: any = {};

  private cambioIdiomaSubject: BehaviorSubject<any>;
  public cambioIdioma$: Observable<any>;


  private _locale: string;

  /**
   * Normaliza códigos de idioma del backend/navegador a la clave del fichero en `assets/i18n/*.json`.
   * P. ej. es-419 (API) → es-ES (existe `es-ES.json`; no existe `es-419.json`).
   */
  setLocale(value: string) {
    const raw = String(value || '').trim();
    const lower = raw.toLowerCase();
    let mapped = raw;
    switch (lower) {
      case 'ca':
        mapped = 'ca';
        break;
      case 'en':
      case 'en-gb':
      case 'en-us':
        mapped = 'en-GB';
        break;
      case 'es':
      case 'es-es':
        mapped = 'es-ES';
        break;
      case 'fr':
      case 'fr-fr':
        mapped = 'fr-FR';
        break;
      case 'pt':
      case 'pt-pt':
        mapped = 'pt-PT';
        break;
      default:
        if (lower.startsWith('es-')) {
          mapped = 'es-ES';
        } else if (lower.startsWith('fr')) {
          mapped = 'fr-FR';
        } else if (lower.startsWith('en')) {
          mapped = 'en-GB';
        } else if (lower.startsWith('pt')) {
          mapped = 'pt-PT';
        }
        break;
    }
    this._locale = mapped;
  }

  getLocale(): string {
    return this._locale || 'es-ES';
  }

  constructor(private http: HttpClient) {


    this.cambioIdiomaSubject = new BehaviorSubject<any>(undefined);
    this.cambioIdioma$ = this.cambioIdiomaSubject.asObservable();
  }

  public get cambioIdiomaValue() {
    return this.cambioIdiomaSubject.value;
  }


  // use(lang: string): Promise<{}> {
  //   this.setLocale(lang);
  //   return new Promise<{}>((resolve, reject) => {
  //     this.fileLangExists(lang).subscribe(existsLangPath =>
  //       {
  //         if(!existsLangPath){
  //           lang = this._locale ||'es-ES';
  //         }
  //     const langPath = `assets/i18n/${lang || this._locale || 'es-ES'}.json`;
  //     this.http.get<{}>(langPath).subscribe(
  //       translation => {
  //         this.data = Object.assign({}, translation || {});
  //         resolve(this.data);
  //       },
  //       error => {
  //         this.data = {};
  //         resolve(this.data);
  //       }
  //     );
  //   });
  //   });
  // }


  use(lang: string): Promise<{}> {
    this.setLocale(lang);
    this.cambioIdiomaSubject.next(false);
    return new Promise<{}>((resolve, reject) => {
      let langFile = this.getLocale();
      this.fileLangExists(langFile).subscribe((existsLangPath) => {
        if (!existsLangPath) {
          langFile = 'es-ES';
        }
        const langPath = `assets/i18n/${langFile}.json`;
        this.http.get<{}>(langPath).subscribe(
          translation => {
            this.data = Object.assign({}, translation || {});
            this.cambioIdiomaSubject.next(true);
            resolve(this.data);
          },
          error => {
            this.data = {};
            this.cambioIdiomaSubject.next(true);
            resolve(this.data);
          }
        );
      });
    });
  }

  /**
   * Returns a translation instantly from the internal state of loaded translation.
   * All rules regarding the current language, the preferred language of even fallback languages will be used except any promise handling.
   */
  public instant(key: string | Array<string>, idioma?: string, interpolateParams?: Object): string | any {
    if (key === 'undefined' || key === null || !key.length) {
      throw new Error(`Parameter "key" required`);
    }
    if (!idioma) {
      idioma = this.getLocale();
    }

    let res = this.getParsedResult(idioma, key, interpolateParams);
    if (isObservable(res)) {
      if (key instanceof Array) {
        let obj: any = {};
        key.forEach((value: string, index: number) => {
          obj[key[index]] = key[index];
        });

        return obj;
      }
      return key;
    } else {
      return res;
    }

  }

  instantV2(key: string, params?: any): string {
		if (!this.data || !this.data[key]) {
			return key;
		}
		let translation = this.data[key];
		if (params) {
			Object.keys(params).forEach((param) => {
				translation = translation.replace(`{{${param}}}`, params[param]);
			});
		}
		return translation;
	}

  // public instant(key: string | Array<string>, interpolateParams?: Object): string | any {
  //   if ( key === 'undefined' || key === null || !key.length) {
  //     throw new Error(`Parameter "key" required`);
  //   }

  //   let res = this.getParsedResult(this.getLocale(), key, interpolateParams);
  //   if (isObservable(res)) {
  //     if (key instanceof Array) {
  //       let obj: any = {};
  //       key.forEach((value: string, index: number) => {
  //         obj[key[index]] = key[index];
  //       });
  //       return obj;
  //     }
  //     return key;
  //   } else {
  //     return res;
  //   }
  // }

  /**
   * Returns the parsed result of the translations
   */
  public getParsedResult(translations: any, key: any, interpolateParams?: Object): any {
    let res: string | Observable<string>;
    if (key instanceof Array) {
      let result: any = {},
        observables: boolean = false;
      for (let k of key) {
        result[k] = this.getParsedResult(translations, k, interpolateParams);
        if (isObservable(result[k])) {
          observables = true;
        }
      }
      if (observables) {
        const sources = key.map(k => isObservable(result[k]) ? result[k] : of(result[k] as string));
        return forkJoin(sources).pipe(
          map((arr: Array<string>) => {
            let obj: any = {};
            arr.forEach((value: string, index: number) => {
              obj[key[index]] = value;
            });
            return obj;
          })
        );
      }
      return result;
    } else if (key !== 'undefined' || key !== null || key.length > 0) {
      return res = this.data[key] || key;
    }
  }

  fileLangExists(lang: string) {
    const url = `assets/i18n/${lang}.json`;
    return this.fileExists(url);
  }

  fileExists(url: string): Observable<boolean> {
    return this.http.get(url)
      .pipe(
        map(response => {
          return true;
        }),
        catchError(error => {
          return of(false);
        })
      );
  }

}

